<script>
  import { getContext, tick } from "svelte";
  import { navigate } from "svelte-routing";
  import { downloadString } from "downloaders";
  import { clickOutside } from "svelte-utilities";

  import Navbar from "#lib/Navbar.svelte";
  import SpinnerIcon from "@local/assets/icons/spinner-lg.svg";
  import ArchiveIcon from "@local/assets/icons/archive.svg";
  import CaretDownIcon from "@local/assets/icons/caret-down.svg";

  import ReviewerJobListCard from "#lib/home/ReviewerJobListCard.svelte";
  import ReviewerJobListCardShpx from "#lib/home/ReviewerJobListCard-shpx.svelte";
  import LabeledToggleButton from "#lib/LabeledToggleButton.svelte";
  import CreateModal from "#lib/home/CreateModal.svelte";
  import FilterInput from "#lib/home/FilterInput.svelte";
  import DownloadModal from "#lib/home/DownloadModal.svelte";
  import UploadModal from "#lib/home/UploadModal.svelte";

  import { profile } from "#src/stores/auth.js";
  import { cloneJob as cj } from "#src/stores/jobs.js";
  import { jobs, filteredJobs, jobArray, jobFilter } from "#src/stores/review-jobs.js";
  import { createGroup, fetchGroup } from "#src/stores/group.js";
  import eb from "#src/extensions/event-bus.js";
  import { myRfqsOnly, draftTallyRequest } from "#src/stores/ui.js";
  import { orderedList } from "@local/extensions/collections/sortable-list.js";
  import getAssignToOptions from "#src/extensions/assign-to-options.js";
  import { api } from "#src/api";
  import { UX_MODE } from "#src/env.js";

  const org = getContext("org");

  let groupData;
  let creationInProgress = false;
  let deletingJobs = {};
  let createButton = "tally";
  let showButtonMenu = false;

  $: jobArr = jobArray(filteredJobs);
  $: mineAllValue = $myRfqsOnly ? "left" : "right";
  $: group = groupData && createGroup(groupData, { realtime: true });
  $: types = transform($group?.types, orderedList);
  $: hasUnfinishedTallyRequest = $draftTallyRequest?.data.name || $draftTallyRequest?.data.files.length > 0;
  $: assignToOptions = $profile ? getAssignToOptions($profile, $org) : [];

  function transform(coll, xf, defaultValue = []) {
    return coll ? xf(coll) : defaultValue;
  }

  function invisibleJob($profile, job) {
    if ($profile?.profile_capabilities?.capabilities?.edit_tally_jobs) {
      return false;
    }

    return job?.status === "tally:canceled";
  }

  async function fetchJobs() {
    await jobs.fetch();
    jobs.subscribeToDbUpdates();
  }

  function toggleShowMine(e) {
    const mine = e.detail.value;
    if (mine === "left") {
      $myRfqsOnly = true;
    } else {
      $myRfqsOnly = false;
    }
  }

  async function archiveJob(e) {
    const { id } = e.detail;
    await jobs.archive(id);
  }

  async function cloneJob(e) {
    const { id } = e.detail;
    const assignedTo = $profile?.profile_capabilities?.capabilities?.switch_organization
      ? $profile?.id
      : null;
    const job = $jobs[id];
    const newJobId = await cj(job.current_group_id, assignedTo);
    jobs.fetchOne(newJobId);
  }

  async function deleteJob(e) {
    const { id } = e.detail;
    deletingJobs[id] = true;
    // Clear the spinner if the delete didn't take, or the row is stuck
    // "deleting" forever while the job is still there.
    if (!(await jobs.remove(id))) deletingJobs[id] = false;
  }

  async function exportFile(e) {
    const { id } = e.detail;
    const job = $jobs[id];
    const exported = await api.rpc("dump_job_to_json", { job_id: job.id });
    await tick();

    if (exported.error) {
      console.error("Error exporting job:", exported.error);
      return;
    }

    downloadString(JSON.stringify(exported.data), `${job.name}.json`);
  }

  async function updateJob(e) {
    const { id, update } = e.detail;
    await api.from("jobs").update(update).eq("id", id).select("*").single();
    jobs.fetchOne(id);
  }

  async function downloadJob(e) {
    const { id } = e.detail;
    const job = $jobs[id];
    groupData = await fetchGroup(job.current_group_id);
    await tick();
    eb.dispatch("open-download");
  }
</script>

<Navbar context="reviewer-job-list" />
{#if $profile && $org}
  <div class="grow bg-gray-100 home-container">
    {#await fetchJobs()}
      <div class="text-gray-600 p-4 h-24 w-24 relative">
        <div class="absolute">
          <div class="animate-spin">
            <SpinnerIcon />
          </div>
        </div>
      </div>
    {:then}
      <div class="list-container">
        <div class="h-full w-full flex flex-col items-center overflow-y-auto">
          <div class="content-container p-4 space-y-4">
            <div class="flex justify-between items-center px-4">
              <div class="w-36">
                <LabeledToggleButton
                  left="Mine"
                  right="All"
                  selected={mineAllValue}
                  on:toggle={toggleShowMine} />
              </div>
              <div class="flex gap-2 items-center">
                <FilterInput bind:filter={$jobFilter} org={$org} />
                <button
                  class="border rounded-md h-8 border-gray-400 px-2 hover:border-gray-600"
                  on:click={() => navigate(`/archive`)}>
                  <ArchiveIcon />
                </button>
              </div>
            </div>
            <div class="space-y-4 overflow-visible">
              {#if creationInProgress}
                <div
                  class="border border-dashed border-gray-400 rounded-md bg-white opacity-50 p-4 flex gap-4 items-center">
                  <div class="text-gray-800 h-8 w-8 relative">
                    <div class="absolute">
                      <div class="animate-spin">
                        <SpinnerIcon />
                      </div>
                    </div>
                  </div>
                  <div class="text-gray-800 italic text-sm">Creating job...</div>
                </div>
              {/if}
              {#each $jobArr as job}
                {#if !invisibleJob($profile, job)}
                  {#if UX_MODE === "shpx"}
                    <ReviewerJobListCardShpx
                      {job}
                      deleting={deletingJobs[job?.id]}
                      {assignToOptions}
                      on:download-job={downloadJob}
                      on:update-job={updateJob}
                      on:archive-job={archiveJob}
                      on:clone-job={cloneJob}
                      on:delete-job={deleteJob}
                      on:export-json={exportFile} />
                  {:else}
                    <ReviewerJobListCard
                      {job}
                      deleting={deletingJobs[job?.id]}
                      {assignToOptions}
                      on:download-job={downloadJob}
                      on:update-job={updateJob}
                      on:archive-job={archiveJob}
                      on:clone-job={cloneJob}
                      on:delete-job={deleteJob}
                      on:export-json={exportFile} />
                  {/if}
                {/if}
              {/each}
            </div>
            <div class="px-4 text-sm">
              <button class="btn-text" on:click={() => navigate(`/archive`)}> Archive </button>
            </div>
          </div>
        </div>
      </div>

      <DownloadModal title="Download Contents" {group} {types} name={$group?.job.name} />
    {/await}
    <div class="flex flex-col gap-2 absolute left-4 bottom-4 md:right-6 md:top-4 md:left-auto md:bottom-auto">
      {#if $org?.tally_request_enabled && UX_MODE !== "shpx"}
        <button
          class="btn btn-primary-alt flex gap-1 items-center relative"
          use:clickOutside
          on:outclick={() => (showButtonMenu = false)}
          on:click={() => navigate("/tally-request")}>
          {#if hasUnfinishedTallyRequest}
            <div>+ Finish Tally Request</div>
          {:else}
            <div>+ New Tally Request</div>
          {/if}
          <button class="p-1" on:click|stopPropagation={() => (showButtonMenu = !showButtonMenu)}>
            <CaretDownIcon />
          </button>
          {#if showButtonMenu}
            <div class="dropdown-container w-full top-0 right-0 mt-8">
              <button
                class="dropdown-button-item"
                on:click|stopPropagation={() => {
                  showButtonMenu = false;
                  eb.dispatch("create-rfq");
                }}>
                + New Job
              </button>
              {#if $profile?.profile_capabilities?.capabilities?.upload_json}
                <button
                  class="dropdown-button-item"
                  on:click|stopPropagation={() => {
                    showButtonMenu = false;
                    eb.dispatch("upload-job");
                  }}>
                  Upload JSON
                </button>
              {/if}
            </div>
          {/if}
        </button>
      {:else}
        <button class="btn btn-primary-alt" on:click={() => eb.dispatch("create-rfq")}> + New Job </button>
      {/if}
    </div>
  </div>

  <CreateModal
    isReviewer
    org={$org}
    on:begin-creating={() => (creationInProgress = true)}
    on:end-creation={() => (creationInProgress = false)} />
  <UploadModal isSupplier={false} org={$org} />
{/if}

<style lang="scss">
  .home-container {
    display: block;
    position: relative;
    overflow-y: hidden;

    .list-container {
      width: 100%;
      height: 100%;
    }

    .content-container {
      width: 100%;
      overflow: visible;

      @media (min-width: 768px) {
        width: 52.5rem;
      }
    }
  }
</style>
